if (!customElements.get('vhalam-infinite-collection')) {
  class VhalamInfiniteCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.list = this.querySelector('.product-list');
      this.pagination = this.querySelector('collection-pagination');
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.message = this.querySelector('[data-infinite-scroll-message]');
      this.nextUrl = this.dataset.nextUrl || '';
      this.currentPage = Number(this.dataset.currentPage || 1);
      this.loading = false;
      this.restoring = true;
      this.waitingForExit = false;
      this.controller = new AbortController();
      this.handlePageHide = this.handlePageHide.bind(this);

      if (!this.list || !this.pagination || !this.status || !('IntersectionObserver' in window)) return;
      const params = new URL(location.href).searchParams;
      if (params.get('sort_by') === 'discounted' || params.has('vhalam_color')) return;

      this.classList.add('collection-products__results--infinite');
      this.stateKey = this.getStateKey();
      this.status.hidden = !this.nextUrl;
      window.addEventListener('pagehide', this.handlePageHide);

      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            this.waitingForExit = false;
            continue;
          }
          if (this.waitingForExit) continue;
          this.waitingForExit = true;
          this.loadNext();
          break;
        }
      }, { rootMargin: '600px 0px' });

      this.restoreState().finally(() => {
        this.restoring = false;
        this.scheduleObserve();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.observeFrame);
      window.removeEventListener('pagehide', this.handlePageHide);
    }

    getStateKey() {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `vhalam-infinite:${this.dataset.infiniteScrollContext || 'products'}:${url.pathname}?${url.searchParams.toString()}`;
    }

    observe() {
      if (!this.nextUrl || this.loading || this.restoring || !this.status) return;
      this.status.hidden = false;
      this.observer?.observe(this.status);
    }

    scheduleObserve() {
      cancelAnimationFrame(this.observeFrame);
      this.observeFrame = requestAnimationFrame(() => {
        this.observeFrame = requestAnimationFrame(() => this.observe());
      });
    }

    handlePageHide() {
      this.saveState(window.scrollY);
    }

    saveState(scrollPosition = window.scrollY) {
      const state = {
        key: this.stateKey,
        page: this.currentPage,
        scrollPosition
      };

      try {
        sessionStorage.setItem(this.stateKey, JSON.stringify(state));
      } catch {}

      history.replaceState({ ...history.state, vhalamInfiniteCollection: state }, '', window.location.href);
    }

    async restoreState() {
      const historyState = history.state?.vhalamInfiniteCollection;
      if (!historyState || historyState.key !== this.stateKey) {
        this.saveState(0);
        return;
      }

      let savedState = historyState;
      try {
        const storedState = JSON.parse(sessionStorage.getItem(this.stateKey));
        if (storedState?.key === this.stateKey) savedState = storedState;
      } catch {}

      const targetPage = Math.max(Number(savedState.page || 1), this.currentPage);
      while (this.currentPage < targetPage && this.nextUrl) {
        const loaded = await this.loadNext({ animate: false, announce: false });
        if (!loaded) break;
      }

      if (Number(savedState.scrollPosition) > 0) {
        requestAnimationFrame(() => window.scrollTo({ top: Number(savedState.scrollPosition), behavior: 'auto' }));
      }
    }

    async loadNext({ animate = true, announce = true } = {}) {
      if (this.loading || !this.nextUrl) return false;

      this.loading = true;
      this.observer?.unobserve(this.status);
      this.setLoading(true);

      try {
        const response = await fetch(new URL(this.nextUrl, window.location.origin), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(String(response.status));

        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const context = this.dataset.infiniteScrollContext || '';
        const nextCollection = Array.from(page.querySelectorAll('vhalam-infinite-collection'))
          .find((element) => element.dataset.infiniteScrollContext === context);
        const nextList = nextCollection?.querySelector('.product-list');
        const nextPagination = nextCollection?.querySelector('collection-pagination');
        if (!nextCollection || !nextList) throw new Error('Missing product results');

        const items = Array.from(nextList.children).map((item, index) => {
          const importedItem = document.importNode(item, true);
          if (animate) {
            importedItem.classList.add('collection-product-reveal');
            importedItem.style.setProperty('--collection-product-reveal-index', String(index % 8));
          }
          return importedItem;
        });

        const fragment = document.createDocumentFragment();
        for (const item of items) fragment.append(item);
        const scrollPosition = window.scrollY;
        this.list.append(fragment);
        window.scrollTo({ top: scrollPosition, behavior: 'auto' });

        this.nextUrl = nextCollection.dataset.nextUrl || '';
        this.currentPage = Number(nextCollection.dataset.currentPage || this.currentPage + 1);
        this.dataset.nextUrl = this.nextUrl;
        this.dataset.currentPage = String(this.currentPage);
        this.pagination.innerHTML = nextPagination?.innerHTML || '';
        this.saveState();

        this.dispatchEvent(new CustomEvent('collection:products-appended', {
          bubbles: true,
          detail: { count: items.length, page: this.currentPage }
        }));

        if (announce) this.announce(this.nextUrl ? this.dataset.loadedLabel : this.dataset.completeLabel);
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return false;
        this.nextUrl = '';
        this.classList.remove('collection-products__results--infinite');
        this.observer?.disconnect();
        this.announce(this.dataset.errorLabel);
        return false;
      } finally {
        this.loading = false;
        this.setLoading(false);
        this.scheduleObserve();
      }
    }

    setLoading(isLoading) {
      this.status.hidden = !isLoading && !this.nextUrl;
      this.status.setAttribute('aria-busy', String(isLoading));
      this.classList.toggle('collection-products__results--loading', isLoading);
      if (isLoading) this.announce(this.dataset.loadingLabel);
    }

    announce(message) {
      if (!this.message || !message) return;
      this.message.textContent = '';
      requestAnimationFrame(() => {
        this.message.textContent = message;
      });
    }
  }

  customElements.define('vhalam-infinite-collection', VhalamInfiniteCollection);
}
